/**
 * Owner-side decrypt + re-wrap for the Dorothy flow's review step.
 *
 * This is the member app's FIRST decryption path. The heavy crypto is the
 * REAL, proven, vendored code — `unwrapCekForRecipient` / `wrapCekForRecipient`
 * / `wrapCekForVaultDek` from `crypto/cek.js` and `importPublicKeyFromServer`
 * from `crypto/keys.js` (the exact functions the backend integration + the
 * recipient app exercise). The only local code is browser-standard glue —
 * reading the owner's device key from IndexedDB and an AES-GCM decrypt — copied
 * verbatim from the proven `src/client/recipient/decrypt.ts` so both paths
 * behave identically.
 *
 * Flow:
 *   decryptOwnerDraft — fetch the owner review-context (owner-wrapped CEK +
 *     presigned ciphertext + iv), open the CEK with the owner's device key,
 *     AES-GCM-decrypt the media, return a playable Blob AND the plaintext CEK
 *     (retained so the owner can immediately re-seal without re-opening).
 *   resealOwnerDraft — re-wrap the CEK to the chosen recipient's pubkey + the
 *     vault DEK (from seal-context), POST the owner seal. Zeroizes the CEK.
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - vendored JS has no types
import { unwrapCekForRecipient, wrapCekForRecipient, wrapCekForVaultDek } from '../../../vendor/crypto/cek.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - vendored JS has no types
import { importPublicKeyFromServer } from '../../../vendor/crypto/keys.js';

import type { ApiClient } from '../apiClient';
import { getSealContext } from '../seal/sealApi';
import { aesGcmDecrypt, base64ToBytes, loadOwnerPrivateKey, toArrayBuffer } from '../ownerDeviceKey';

export interface OwnerReviewContext {
  message_id: string;
  format: string;
  cek_ciphertext: string;
  cek_algorithm_version: string | null;
  media: {
    download_url: string;
    iv_b64: string;
    mime_type: string;
    container: string | null;
  };
}

export interface OwnerDraftPayload {
  blob: Blob;
  /** Plaintext 32-byte CEK, retained for an immediate re-seal. Zeroize after. */
  cek: Uint8Array;
  mimeType: string;
  container: string | null;
}

export type DeliveryKeyHolder = 'recipient' | 'owner';

export interface RecipientSealKey {
  public_key: string;
  public_key_suite: string;
  delivery_key_holder: DeliveryKeyHolder;
}

export function getReviewContext(
  api: ApiClient,
  vaultId: string,
  messageId: string,
): Promise<OwnerReviewContext> {
  return api.get(`/api/vaults/${vaultId}/senior-recordings/${messageId}/review-context`);
}

/**
 * Resolve which key to wrap the CEK to for a chosen recipient — the recipient's
 * own key, or the owner-fallback for a placeholder/keyless recipient (delivery
 * re-wraps on unlock). Mirrors the normal seal's resolveRecipientKey.
 */
export function getRecipientSealKey(
  api: ApiClient,
  vaultId: string,
  recipientId: string,
): Promise<RecipientSealKey> {
  return api.get(
    `/api/vaults/${vaultId}/senior-recordings/recipients/${recipientId}/seal-key`,
  );
}

/**
 * Open a queued senior recording with the owner's device key and return a
 * playable Blob + the plaintext CEK (for the subsequent re-seal).
 */
export async function decryptOwnerDraft(args: {
  api: ApiClient;
  vaultId: string;
  messageId: string;
  ownerEmail: string;
  /** Test injection: provide the owner CryptoKey directly. */
  privateKeyOverride?: CryptoKey;
  fetchImpl?: typeof fetch;
}): Promise<OwnerDraftPayload> {
  const fetchImpl = args.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const ctx = await getReviewContext(args.api, args.vaultId, args.messageId);

  const iv = base64ToBytes(ctx.media.iv_b64);
  if (iv.byteLength !== 12) {
    throw new Error(`Review-context IV must be 12 bytes, got ${iv.byteLength}.`);
  }

  const resp = await fetchImpl(ctx.media.download_url);
  if (!resp.ok) {
    throw new Error(`Encrypted recording fetch failed (HTTP ${resp.status}).`);
  }
  const ciphertext = new Uint8Array(await resp.arrayBuffer());

  const privKey =
    args.privateKeyOverride ?? (await loadOwnerPrivateKey(args.ownerEmail));

  // Open the owner-wrapped CEK (generic HPKE open, binds to messageId).
  const cek: Uint8Array = await unwrapCekForRecipient(
    ctx.cek_ciphertext,
    privKey,
    args.messageId,
  );

  let plaintext: Uint8Array;
  try {
    plaintext = await aesGcmDecrypt({
      key: cek,
      iv,
      ciphertext,
      aad: new TextEncoder().encode(args.messageId),
    });
  } catch (err) {
    // A tampered/corrupt ciphertext fails the GCM tag — don't leave the CEK.
    cek.fill(0);
    throw err;
  }

  // Derive the playback MIME from the CONTAINER, not the stored mime_type: a
  // senior webm recording can be mislabeled video/mp4 at submit (codec hardcoded),
  // which makes <video> reject it with a format error. The container is truthful.
  const playbackMime =
    ctx.media.container === 'mp4'
      ? 'video/mp4'
      : ctx.media.container === 'webm'
        ? 'video/webm'
        : ctx.media.mime_type;
  // toArrayBuffer copies the bytes into the Blob; zero the working plaintext.
  const blob = new Blob([toArrayBuffer(plaintext)], { type: playbackMime });
  plaintext.fill(0);
  return { blob, cek, mimeType: playbackMime, container: ctx.media.container };
}

/**
 * Re-seal a reviewed draft to a recipient: re-wrap the (already-opened) CEK to
 * the recipient's pubkey + the vault DEK, then POST the owner seal. The CEK is
 * zeroized on the way out.
 */
export async function resealOwnerDraft(args: {
  api: ApiClient;
  vaultId: string;
  messageId: string;
  cek: Uint8Array;
  recipientId: string;
}): Promise<void> {
  let vaultDek: Uint8Array | null = null;
  try {
    // Resolve the target key server-side (recipient's own, or owner-fallback for
    // a placeholder/keyless recipient) BEFORE wrapping — mirrors the normal seal.
    const resolved = await getRecipientSealKey(args.api, args.vaultId, args.recipientId);
    const ctx = await getSealContext(args.api, args.messageId);
    vaultDek = base64ToBytes(ctx.vault_dek_b64);
    if (vaultDek.byteLength !== 32) {
      throw new Error('Vault DEK from server is not 32 bytes; aborting re-seal.');
    }
    const recipientKey = await importPublicKeyFromServer(resolved.public_key);
    const cekRecipientWrap: string = await wrapCekForRecipient(
      args.cek,
      recipientKey.publicKey,
      args.messageId,
    );
    const cekVaultWrap: string = await wrapCekForVaultDek(args.cek, vaultDek, args.messageId);

    await args.api.post(
      `/api/vaults/${args.vaultId}/senior-recordings/${args.messageId}/seal`,
      {
        recipient_id: args.recipientId,
        cek_recipient_wrap: cekRecipientWrap,
        cek_vault_wrap: cekVaultWrap,
        recipient_public_key: resolved.public_key,
        delivery_key_holder: resolved.delivery_key_holder,
        cek_algorithm_version: ctx.cipher_suite,
      },
    );
  } finally {
    if (vaultDek !== null) vaultDek.fill(0);
    args.cek.fill(0);
  }
}

export function discardOwnerDraft(
  api: ApiClient,
  vaultId: string,
  messageId: string,
): Promise<{ discarded: true }> {
  return api.post(`/api/vaults/${vaultId}/senior-recordings/${messageId}/discard`, {});
}

